'use client';

import { useTranslation } from 'next-i18next';
import { Button, Anchor } from '@components/common';
import { useSidebarNavigation } from '@components/contexts/SidebarNavigationContext';
import { FloatingNavBar } from './FloatingNavBar';
import { FloatingNavBarOverflowMenu } from './FloatingNavBarOverflowMenu';

export type CandidateNavButton = 'new-job' | 'coaching' | 'edit-profile' | 'my-bestie';

const SELECTED_CLASS = '!border-ink !bg-primary/10 !text-ink shadow-none hover:!border-ink hover:!bg-primary/10 hover:!text-ink focus:!ring-0';
const DEFAULT_CLASS = '!border-transparent !bg-primary/10 !text-ink shadow-none hover:!border-transparent hover:!bg-primary/20 hover:!text-ink focus:!ring-0';
const BTN_CLASS = '!h-8 !px-2.5 text-xs !rounded-2xl lg:!h-11 lg:!px-8 lg:text-lg whitespace-nowrap';


/**
 * @description Candidate-specific floating nav bar shown on all candidate MC views.
 *
 * Buttons (left → right):
 * - **New Job** (promo) — navigates to the candidate job search form.
 *   Hidden only while the search form is visible (the form has its own submit button).
 *   Remains visible on the job results page so the user can start a new search.
 * - **Coaching** — placeholder, navigates to career coaching (TBD).
 * - **Edit Profile** — opens the CV/profile edit page.
 * - **My Bestie Agent** — navigates to the My Bestie landing view.
 *
 * Pass `selectedButton` to mark a button as the current page (adds border, disables click).
 */
export const CandidateFloatingNavBar = ({
  locale,
  selectedButton,
  pillBackground,
  inline,
  isAuthenticated = false,
}: {
  locale: Locale;
  selectedButton?: CandidateNavButton;
  pillBackground?: 'surface' | 'surface-100';
  inline?: boolean;
  isAuthenticated?: boolean;
}) => {
  const { t } = useTranslation('common');
  const { currentView, navigateToNewJobSearchForm, navigateToMyBestieLanding } = useSidebarNavigation();

  const isSearchForm = currentView === 'new-job-search-form'
    || (typeof currentView === 'object' && 'editSearch' in currentView);

  const editProfileHref = isAuthenticated
    ? `/${locale}/mc/profile`
    : `/login?_target_path=${encodeURIComponent(`/${locale}/mc/profile`)}`;

  return (
    <FloatingNavBar pillBackground={pillBackground} inline={inline}>
      <FloatingNavBarOverflowMenu isVisible disableOverflow>
        {/* New Job — hidden while the search form is visible (the form has its own submit button) */}
        {!isSearchForm && (
          <Button
            type="button"
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${DEFAULT_CLASS}`}
            onClick={navigateToNewJobSearchForm}
          >
            {t('mc.search.job')}
          </Button>
        )}

        {/* Coaching */}
        {selectedButton === 'coaching' ? (
          <Button
            type="button"
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${SELECTED_CLASS}`}
          >

            {t('mc.career.coaching')}
          </Button>
        ) : (
          <Anchor
            href={`/${locale}/mc`}
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${DEFAULT_CLASS}`}
          >
            {t('mc.career.coaching')}
          </Anchor>
        )}

        {/* Edit Profile */}
        {selectedButton === 'edit-profile' ? (
          <Button
            type="button"
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${SELECTED_CLASS}`}
          >
            {t('global.label.cv.edit')}
          </Button>
        ) : (
          <Anchor
            href={editProfileHref}
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${DEFAULT_CLASS}`}
          >
            {t('global.label.cv.edit')}
          </Anchor>
        )}

        <Button
          type="button"
          styling="solid"
          color="light"
          className={`${BTN_CLASS} ${selectedButton === 'my-bestie' ? SELECTED_CLASS : DEFAULT_CLASS}`}
          onClick={selectedButton === 'my-bestie' ? undefined : navigateToMyBestieLanding}
        >
          {t('mc.personal.agent')}
        </Button>
      </FloatingNavBarOverflowMenu>
    </FloatingNavBar>
  );
};
